import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Star, CheckCircle2, Play } from "lucide-react";
import {
  ASSETS,
  TESTIMONIALS,
  RESULT_SCREENSHOTS,
  WRITTEN_REVIEWS,
} from "../mock";
import { track } from "../analytics";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Live 5-star reviews strip (auto-scrolling social proof) ---------- */
export const LiveReviewsStrip = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews/wall`, { params: { limit: 24 } })
      .then((res) => {
        if (alive) setReviews(res.data?.reviews || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!reviews.length) return null;

  return (
    <section className="live-reviews-section" data-testid="live-reviews-strip">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={16} className="text-[#16E36F]" fill="#16E36F" />
            ))}
          </span>
          <p className="text-sm text-neutral-400">
            <span className="font-bold text-[#16E36F]">4.92/5</span> from{" "}
            <span className="font-bold text-[#16E36F]">1,500+</span> verified
            member reviews
          </p>
        </div>
      </div>

      <div className="live-reviews-marquee mt-8">
        <div className="live-reviews-track">
          {[0, 1, 2].map((copy) => (
            <div className="live-reviews-group" key={copy} aria-hidden={copy > 0}>
              {reviews.map((r, i) => (
                <article className="live-review-card" key={`${copy}-${i}`}>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, s) => (
                      <Star
                        key={s}
                        size={13}
                        className="text-[#16E36F]"
                        fill="#16E36F"
                      />
                    ))}
                  </div>
                  <p className="mt-2.5 text-[14px] leading-relaxed text-neutral-300">
                    {r.review.length > 220
                      ? `${r.review.slice(0, 220).trim()}…`
                      : r.review}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="live-review-avatar">
                      {(r.username || "?").charAt(0).toUpperCase()}
                    </span>
                    <span className="text-[13px] font-semibold text-white">
                      {r.username}
                    </span>
                    <span className="ml-auto text-[11px] font-medium text-[#16E36F]">
                      Verified
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Top rotating banner (same texts & style as original) ---------- */
const BANNER_TEXTS = [
  "LIMITED SPOTS AVAILABLE",
  "100% REFUND GUARANTEE",
  "LIFETIME ACCESS TO PROPRIETARY TRADING KNOWLEDGE & SOFTWARE",
];

export const MarqueeBanner = () => {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // texts stay readable for ~4.5s, with a smooth 0.7s cross-fade
    const timer = setInterval(() => {
      setVisible(false);
      const swap = setTimeout(() => {
        setIndex((i) => (i + 1) % BANNER_TEXTS.length);
        setVisible(true);
      }, 700);
      return () => clearTimeout(swap);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="rotating-banner">
      <span className="rotating-dot" />
      <span
        className="rotating-text"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0px)" : "translateY(-5px)",
        }}
      >
        {BANNER_TEXTS[index]}
      </span>
    </div>
  );
};

/* ---------- Testimonial video card: Vidalytics portrait embed above name/flag ---------- */
const VIDALYTICS_ACCOUNT = "05PByEt0";

const VidalyticsEmbed = ({ videoId, ratio = 177.78 }) => {
  const embedDivId = `vidalytics_embed_${videoId}`;
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const init = () => {
      const initialized =
        window.__vidalyticsInit || (window.__vidalyticsInit = new Set());
      if (initialized.has(videoId)) return;
      initialized.add(videoId);
      const base = `https://fast.vidalytics.com/embeds/${VIDALYTICS_ACCOUNT}/${videoId}/`;
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.text = `(function (v, i, d, a, l, y, t, c, s) {
        y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
        if (!vsl){vsl=function(u,cb){
          if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
          if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
          i.getElementsByTagName("head")[0].appendChild(s);
        };}
        vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
      })(window, document, 'Vidalytics', '${embedDivId}', '${base}');`;
      document.body.appendChild(script);
    };
    // Defer embed init until it scrolls near the viewport (faster first load)
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            init();
            obs.disconnect();
          }
        });
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [videoId, embedDivId]);

  return (
    <div
      ref={containerRef}
      id={embedDivId}
      style={{ width: "100%", position: "relative", paddingTop: `${ratio}%` }}
    />
  );
};

// Click-to-play facade: the Vidalytics embed only mounts after the user
// clicks, so testimonial videos never start automatically on load/scroll.
const VidalyticsFacade = ({ videoId, ratio = 177.78, name, poster }) => {
  const [play, setPlay] = useState(false);
  if (play) return <VidalyticsEmbed videoId={videoId} ratio={ratio} />;
  return (
    <button
      type="button"
      onClick={() => {
        setPlay(true);
        track("cta_click", { cta: "testimonial_video", video: videoId });
      }}
      aria-label={`Play testimonial${name ? " from " + name : ""}`}
      data-testid={`testimonial-play-${videoId}`}
      className="group/vid relative block w-full overflow-hidden bg-black"
      style={{ paddingTop: `${ratio}%` }}
    >
      {poster ? (
        <img
          src={poster}
          alt={name ? `${name} testimonial` : "Testimonial"}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <span className="absolute inset-0 bg-gradient-to-b from-[#0b1a11] via-[#07130c] to-black" />
      )}
      {/* subtle darken for button legibility */}
      <span className="absolute inset-0 bg-black/10 transition-colors duration-200 group-hover/vid:bg-black/25" />
      {/* clean circular play button */}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex h-[74px] w-[74px] items-center justify-center rounded-full bg-white/95 text-black shadow-[0_8px_30px_rgba(0,0,0,0.45)] ring-1 ring-black/5 transition-transform duration-200 group-hover/vid:scale-110">
          <Play size={30} fill="#0a0f0b" strokeWidth={0} className="ml-1" />
        </span>
      </span>
      {name && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1 text-xs font-semibold uppercase tracking-[0.15em] text-white backdrop-blur-sm">
          {name}
        </span>
      )}
    </button>
  );
};

const VideoCard = ({ t }) => (
  <div className="testimonial-card group">
    <div className="testimonial-embed-wrap" data-testid={`video-card-${t.id}`}>
      <VidalyticsFacade videoId={t.videoId} ratio={t.ratio} name={t.name} poster={t.poster} />
    </div>
    <div className="flex flex-col items-center pt-4 text-center">
      <div className="flex items-center gap-2.5">
        <img
          src={t.flag}
          alt=""
          loading="lazy"
          className="h-[18px] w-auto rounded-[3px] border border-white/10"
        />
        <span className="text-xl font-semibold text-white">{t.name}</span>
      </div>
      <div className="mt-1.5 text-xs font-medium uppercase tracking-[0.2em] text-[#16E36F]">
        {t.role}
      </div>
    </div>
  </div>
);

/* ---------- Founders section: who's behind The Matrix Unlocked ---------- */
/* Alternating image/text split used in the founder story (Z-pattern) */
const StorySplit = ({ image, alt, testid, imageRight, children }) => (
  <div className="mt-14 grid grid-cols-1 items-stretch gap-8 lg:mt-20 lg:grid-cols-2 lg:gap-16">
    <div className={`order-1 ${imageRight ? "lg:order-2" : "lg:order-1"}`}>
      <img
        src={image}
        alt={alt}
        loading="lazy"
        className="h-full max-h-[520px] w-full rounded-2xl border border-white/10 object-cover"
        data-testid={testid}
      />
    </div>
    <div
      className={`order-2 flex flex-col justify-center ${
        imageRight ? "lg:order-1" : "lg:order-2"
      }`}
    >
      {children}
    </div>
  </div>
);


export const FoundersSection = () => {
  const scrollToForm = (e) => {
    e.preventDefault();
    track("cta_click", { cta: "founder_see_if_i_qualify" });
    document
      .getElementById("typeform")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section className="founders-section" id="founders">
      <div className="mx-auto max-w-6xl px-5 text-pretty">
        <span className="founder-eyebrow">WHO&rsquo;S BEHIND THE MATRIX UNLOCKED</span>

        {/* --- Block 1: Understand What Is Actually Taking Place --- */}
        <StorySplit
          image={ASSETS.frankPortrait}
          alt="Frank Warrink, founder of The Matrix Unlocked"
          testid="founder-portrait"
          imageRight
        >
          <h2 className="text-balance text-3xl font-semibold leading-tight text-white sm:text-4xl">
            Understand What Is Actually Taking Place
          </h2>
          <div className="mt-6 space-y-4 text-lg leading-relaxed text-neutral-300">
            <p>
              If you&rsquo;ve been{" "}
              <span className="font-semibold text-[#EF4444]">
                blaming your psychology
              </span>{" "}
              for why you&rsquo;re not where you want to be in your trading
              journey, there&rsquo;s a question worth asking first:
            </p>
            <p className="font-semibold italic text-white">
              How well do you understand what is actually taking place in the
              market?
            </p>
            <p>
              It&rsquo;s difficult to trust your decisions when you cannot
              explain why you expect Price to move toward a particular level, or
              when that move becomes relevant.
            </p>
            <p>
              I&rsquo;m Frank Warrink&nbsp;&mdash; also known online as{" "}
              <span className="font-semibold text-[#16E36F]">Frank369</span>{" "}
              (@FrankOf369)&nbsp;&mdash; founder of{" "}
              <span className="font-semibold text-[#16E36F]">The Matrix Unlocked</span>. My approach brings together Time Cycles, Price Cycles,
              Liquidity, Market Maker Models and Imbalances to see how Price
              moves.
            </p>
            <p className="font-semibold text-white">
              Time and Price is the foundation of what we teach inside{" "}
              <span className="whitespace-nowrap text-[#16E36F]">The Matrix Unlocked</span>.
            </p>
          </div>
        </StorySplit>

        {/* --- Block 3: See Time and Price in Execution (full-width, uncropped charts) --- */}
        <div className="mt-14 lg:mt-20">
          <div className="max-w-3xl">
            <h2 className="text-balance text-3xl font-semibold leading-tight text-white sm:text-4xl">
              See Time and Price in Execution
            </h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-neutral-300">
              <p>Below are trades based upon Time &amp; Price.</p>
              <p>
                Have you ever seen anything which comes remotely close to these
                executions?
              </p>
            </div>
          </div>
          <div className="mt-8 space-y-6">
            <img
              src={ASSETS.tradeExec1}
              alt="Time and Price trade execution example"
              loading="lazy"
              className="w-full rounded-2xl border border-white/10"
              data-testid="trade-exec-1"
            />
            <img
              src={ASSETS.tradeExec2}
              alt="Time and Price trade execution example"
              loading="lazy"
              className="w-full rounded-2xl border border-white/10"
              data-testid="trade-exec-2"
            />
          </div>
        </div>

        {/* --- Block 4: Learn the Process Behind the Trades --- */}
        <StorySplit
          image={ASSETS.coaching}
          alt="Personal coaching inside The Matrix Unlocked"
          testid="coaching-image"
        >
          <h2 className="text-balance text-3xl font-semibold leading-tight text-white sm:text-4xl">
            Learn the Process Behind the Trades
          </h2>
          <div className="mt-6 space-y-4 text-lg leading-relaxed text-neutral-300">
            <p>
              I created{" "}
              <span className="font-semibold text-[#16E36F]">The Matrix Unlocked</span>{" "}
              to teach the Time and Price framework
              behind my trading, with a structured program and personal coaching
              to help you apply it yourself.
            </p>
            <p>
              Over 12 weeks, you work through educational lessons and homework,
              supported by personal coaching and an accountability partner. Your
              coach examines your analysis with you, identifies gaps in your
              understanding, and helps you focus on what you need to practice
              next.
            </p>
            <p>
              You also gain access to exclusive algorithmic software and a
              trading journal for measured progress, alongside lifetime community
              and educational library access and private in-person events.
            </p>
            <p>
              The lessons give you material to study. The homework puts your
              understanding into practice. Coaching gives you the opportunity to
              have your reasoning questioned and your mistakes explained.
            </p>
            <p className="font-semibold text-white">
              Explore the program and see whether{" "}
              <span className="font-semibold text-[#16E36F]">The Matrix Unlocked</span>{" "}
              is a fit for your trading journey.
            </p>
            <div className="pt-2">
              <a
                href="#typeform"
                onClick={scrollToForm}
                data-testid="coaching-book-call-btn"
                className="inline-flex items-center justify-center rounded-xl bg-[#16E36F] px-7 py-3.5 text-base font-bold text-black transition-colors duration-200 hover:bg-[#13c862]"
              >
                Book Free Call
              </a>
            </div>
          </div>
        </StorySplit>

        <img
          src={ASSETS.groupEvent}
          alt="The Matrix Unlocked community at a private in-person event"
          loading="lazy"
          className="mt-14 w-full rounded-2xl border border-white/10 lg:mt-20"
          data-testid="group-event-image"
        />

        <div className="mt-10 text-center">
          <a
            href="#typeform"
            onClick={scrollToForm}
            className="shimmer-cta inline-flex"
            data-testid="founder-cta"
          >
            <span className="shimmer-cta-inner">Book Free Call</span>
          </a>
        </div>

        {/* --- Offer Stack --- */}
        <div className="mx-auto mt-16 max-w-2xl" data-testid="offer-stack">
          <div className="rounded-2xl border border-[#16E36F]/25 bg-white/[0.03] p-7 sm:p-9">
            <div className="text-center">
              <span className="founder-eyebrow">EVERYTHING INCLUDED IN</span>
              <h3 className="mt-3 text-balance text-2xl font-bold leading-tight text-[#16E36F] sm:text-3xl">
                The Matrix Unlocked
              </h3>
            </div>
            <ul className="mt-7 space-y-3">
              {[
                "12 Weeks of Personalized 1:1 Coaching",
                "Accountability Partner",
                "12-Week Educational Program with educational and homework lectures",
                "Mechanical TMU Protocol",
                "Reversal Sequence Training in Time & Price",
                "Exclusive Algorithmic Trading Tools",
                "TMU Trading Journal",
                "Private Community Access",
                "Private In-Person Events",
                "$90,000 Instant Funding",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 border-b border-white/5 pb-3 text-base font-semibold leading-relaxed text-white last:border-0 last:pb-0"
                >
                  <CheckCircle2
                    size={20}
                    className="shrink-0 text-[#16E36F]"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Testimonials section ---------- */
export const Testimonials = () => (
  <section className="testimonials-section" id="testimonials">
    <div className="mx-auto max-w-6xl px-5">
      <div className="mx-auto mb-6 flex justify-center">
        <img
          src={ASSETS.logoEmblem}
          alt="The Matrix Unlocked"
          className="h-20 w-auto"
        />
      </div>
      <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold leading-tight text-white sm:text-4xl md:text-[44px]">
        Results <span className="text-[#16E36F]">Speak Louder</span> Than
        Words. <span className="text-[#16E36F]">Real</span> traders.{" "}
        <span className="text-[#16E36F]">Real</span> executions.{" "}
        <span className="text-[#16E36F]">Real</span> perspective shifts.
      </h2>

      <div className="mt-10 flex flex-col items-center gap-3">
        <img
          src={ASSETS.avatarsStrip}
          alt="Students of The Matrix Unlocked"
          className="h-14 w-auto sm:h-16"
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-400">
            Trusted by{" "}
            <span className="font-bold text-[#16E36F]">400+</span> students
          </span>
          <span className="flex items-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={14}
                className="text-[#16E36F]"
                fill="#16E36F"
              />
            ))}
          </span>
          <span className="text-sm font-bold text-[#16E36F]">4,9</span>
        </div>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
        {TESTIMONIALS.slice(0, 9).map((t) => (
          <VideoCard key={t.id} t={t} />
        ))}
      </div>

      {/* Roy centered directly under Kurtis (middle column) */}
      <div className="mt-10 flex justify-center">
        <div className="w-full sm:w-1/2 lg:w-[calc((100%-5rem)/3)]">
          <VideoCard t={TESTIMONIALS[9]} />
        </div>
      </div>
    </div>
  </section>
);

/* ---------- CTA section: Book My Call with moving results wall under it ---------- */
export const CTASection = () => {
  const scrollToForm = (e) => {
    e.preventDefault();
    track("cta_click", { cta: "book_my_call" });
    document
      .getElementById("typeform")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section className="cta-section">
      <div className="relative mx-auto max-w-5xl px-5 text-center">
        <a href="#typeform" onClick={scrollToForm} className="shimmer-cta">
          <span className="shimmer-cta-inner">Book Free Call</span>
        </a>
      </div>
    </section>
  );
};

/* ---------- Student YouTube interviews (from thematrixunlocked.com) ---------- */
const STUDENT_INTERVIEWS = [
  { id: "1nl3idnUcBA", title: "How Kristyn Went On An Insane 23 Win Streak (Using TMU Protocol)" },
  { id: "cFSbmmMVr3g", title: "How I Helped My Student Make $12,934 in a Single Payout" },
  { id: "dMoVoRUPfwk", title: "I Helped My Student Make Over $22,957 In 14 Days" },
  { id: "jyI_O_UC7S0", title: "Payout In 8 Weeks After Failing For Years" },
  { id: "SLCXM0ykinQ", title: "How This 22 Year Old MMXM Trader Decoded The Algo" },
  { id: "HKoCk2vbvLw", title: "From Senior Specialist To Market Wizard | The Story Of Gerard" },
  { id: "M9V0XPuiSR8", title: "How Trading Objectively Helped Alessandro Avoid Costly Mistakes" },
  { id: "Y6ivuiOjqyE", title: "Time Cycles Changed His Life" },
  { id: "2EtGiIt8qW4", title: "Paul Unlocked The Matrix At 60 Years Old" },
  { id: "LABJ9ZS82ak", title: "Rafi Quit Oil & Gas Business To Pursue Trading" },
  { id: "pgPnEMcAnrk", title: "What It Takes To Become a Full Time Trader At 22" },
  { id: "szjrFj08mXI", title: "He Lost It All, And Made It All Back Using These Steps" },
  { id: "GXANt0wC9cs", title: "Lessons After 18 Years Of Trading" },
  { id: "G94xQMbj2KA", title: "I Sat Down With Jamie" },
  { id: "ZdvGyeuOqSg", title: "A 30 Minute Conversation That Changes Your Trading Career" },
];

// Lightweight YouTube facade: shows the thumbnail and only loads the iframe
// on click, so 15 videos never slow down the initial ad-traffic page load.
const YouTubeInterview = ({ id, title }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="group" data-testid={`student-interview-${id}`}>
      <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-white/10 bg-neutral-900">
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
            title={title}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setPlaying(true);
              track("cta_click", { cta: "student_interview", video: id });
            }}
            aria-label={`Play interview: ${title}`}
            data-testid={`student-interview-play-${id}`}
            className="absolute inset-0 h-full w-full bg-cover bg-center"
            style={{
              backgroundImage: `url(https://i.ytimg.com/vi/${id}/hqdefault.jpg)`,
            }}
          >
            <span className="absolute inset-0 flex items-center justify-center transition-colors duration-200 group-hover:bg-black/10">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#16E36F] text-black shadow-[0_6px_24px_rgba(0,0,0,0.55)] transition-transform duration-200 group-hover:scale-110">
                <Play size={26} fill="currentColor" className="ml-0.5" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="mt-3 text-center text-sm font-medium leading-snug text-white">
        {title}
      </div>
    </div>
  );
};

/* ---------- Results screenshot wall (infinite, gapless scroll) ---------- */
export const ResultsWall = () => {
  // Split screenshots across the two rows so none is duplicated between rows.
  // Each row is tripled in the markup for a seamless infinite loop.
  const half = Math.ceil(RESULT_SCREENSHOTS.length / 2);
  const rows = [
    RESULT_SCREENSHOTS.slice(0, half),
    RESULT_SCREENSHOTS.slice(half),
  ];
  return (
    <section className="results-section">
      <div className="mx-auto max-w-6xl px-5">
        <h2 className="text-center text-3xl font-semibold leading-tight text-white sm:text-4xl">
          TMU Student Results
        </h2>
        <p className="mt-3 text-center text-lg text-neutral-400">
          Real Results of{" "}
          <span className="font-semibold text-[#16E36F]">The Matrix Unlocked</span>{" "}
          Students
        </p>
      </div>
      <div className="mt-10 space-y-5">
        {rows.map((row, r) => (
          <div className="results-marquee" key={r}>
            <div
              className={`results-track ${r === 1 ? "results-track-reverse" : ""}`}
            >
              {[0, 1, 2].map((copy) => (
                <div className="results-group" key={copy} aria-hidden={copy > 0}>
                  {row.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt="Student trading results"
                      loading="lazy"
                      className="results-img"
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Student YouTube interviews */}
      <div className="mx-auto mt-16 max-w-6xl px-5 lg:mt-20">
        <h3 className="text-center text-2xl font-semibold leading-tight text-white sm:text-3xl">
          Student Interviews
        </h3>
        <p className="mt-3 text-center text-lg text-neutral-400">
          Hear directly from{" "}
          <span className="font-semibold text-[#16E36F]">The Matrix Unlocked</span>{" "}
          students
        </p>
        <div
          className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="student-interviews-grid"
        >
          {STUDENT_INTERVIEWS.map((v) => (
            <YouTubeInterview key={v.id} id={v.id} title={v.title} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Written reviews (non-video testimonials) ---------- */
export const ReviewsSection = () => {
  return (
    <section className="reviews-section">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="text-4xl font-bold text-white sm:text-5xl">
            <span className="text-[#16E36F]">4.92</span>
            <span className="text-neutral-500">/5</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="revs-stars flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="rev-star text-[#16E36F]"
                  fill="#16E36F"
                  style={{ "--i": i }}
                />
              ))}
            </span>
            <span className="text-sm text-neutral-400">
              Rating on{" "}
              <strong className="font-bold text-[#16E36F]">1300+</strong> Reviews
            </span>
          </div>
        </div>

        <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3">
          {WRITTEN_REVIEWS.map((r) => (
            <div className="review-card" key={r.name}>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="text-[#16E36F]" fill="#16E36F" />
                ))}
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-neutral-300">
                {r.text}
              </p>
              <div className="mt-4 text-sm font-semibold text-white">{r.name}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Bottom CTA: back to the application form ---------- */
export const BottomCTA = () => {
  const scrollToForm = (e) => {
    e.preventDefault();
    track("cta_click", { cta: "bottom_apply" });
    document
      .getElementById("typeform")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <section className="bottom-cta-section">
      <div className="mx-auto max-w-4xl px-5 text-center">
        <h2 className="text-3xl font-semibold leading-tight text-white sm:text-4xl">
          Ready to <span className="text-[#16E36F]">Unlock The Matrix</span>?
        </h2>
        <p className="mt-3 text-neutral-400">
          Apply now and see if you qualify to become part of The Matrix
          Unlocked.
        </p>
        <a
          href="#typeform"
          onClick={scrollToForm}
          className="shimmer-cta mt-8"
          data-testid="bottom-cta"
        >
          <span className="shimmer-cta-inner">Book Free Call</span>
        </a>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
export const Footer = () => (
  <footer className="site-footer">
    <div className="mx-auto max-w-6xl px-5">
      <div className="flex flex-col items-center gap-8">
        <a href="/" aria-label="The Matrix Unlocked home">
          <img
            src={ASSETS.logoWordmark}
            alt="The Matrix Unlocked"
            className="h-12 w-auto sm:h-14"
          />
        </a>
        <nav className="flex items-center gap-8 text-sm">
          <a href="https://thematrixunlocked.com/testimonials" className="footer-link">
            Testimonials
          </a>
          <a href="https://thematrixunlocked.com/about" className="footer-link">
            About
          </a>
          <a href="https://thematrixunlocked.com/faq" className="footer-link">
            FAQ
          </a>
        </nav>
      </div>
      <div className="mt-10 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 sm:flex-row">
        <p className="text-xs text-neutral-500">
          © 2026 Copyright The Matrix Unlocked. All rights reserved.
        </p>
        <p className="sr-only">
          The Matrix Unlocked is the trading education program and community
          founded by Frank Warrink, known online as Frank369 (@FrankOf369) and
          referred to by some traders as Team369 or Team 369.
        </p>
        <div className="flex items-center gap-6 text-xs">
          <Link to="/privacy-policy" className="footer-link">
            Privacy Policy
          </Link>
          <Link to="/terms" className="footer-link">
            Terms of Service
          </Link>
          <Link to="/disclaimer" className="footer-link">
            Disclaimer
          </Link>
        </div>
      </div>
    </div>
  </footer>
);
